/* Bookstore-admin section for the Admin → Users & Tokens panel.
 *
 * Two stacked cards:
 *   1. "Bookstore Admin login" — explains that the The Loom admin IS
 *      the bookstore admin (no separate role) and provides a one-click
 *      rotate-password button. Calls the existing `/api/admin/password-
 *      reset`; delivery by email is required and plaintext is never
 *      returned to the browser.
 *
 *   2. "Bookstore customers" — table of `bookstore_users` with a per-row
 *      "Reset password" affordance. A one-hour single-use link is
 *      delivered by email; credentials and bearer links never enter
 *      the admin browser.
 *
 * All write endpoints already log to `admin_audit`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Loader2, KeyRound, Mail, RotateCcw, Search, ShoppingBag, ChevronDown, ChevronUp,
  Trash2, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { openConfirm } from '@/components/admin/ConfirmDialog';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

function AdminPasswordCard() {
  const [busy, setBusy] = useState(false);
  const [deliverTo, setDeliverTo] = useState('');

  const rotate = async () => {
    if (!(await openConfirm({
      title: 'Send an admin reset link?',
      description: 'A one-time reset link will be delivered to the email address below. No password is exposed in this browser.',
      confirmLabel: 'Send reset link',
      tone: 'destructive',
    }))) return;
    setBusy(true);
    try {
      const r = await http.post('/admin/password-reset', { deliver_to: deliverTo })
        .then((res) => res.data);
      toast.success(`Reset link emailed to ${r.deliver_to || deliverTo}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotation failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="admin-bookstore-admin-card">
      <div className="mb-2 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-primary" />
        <div className="text-[12.5px] font-semibold">Bookstore Admin login</div>
      </div>
      <p className="mb-3 text-[11.5px] text-muted-foreground">
        The The Loom admin <i>is</i> the Bookstore Admin — there&apos;s no separate role.
        Full administrators may request a reset only for their own credential. A one-time link
        is delivered by email and no password is exposed in this browser.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <label className="mb-1 block text-[11px] text-muted-foreground">
            Email the reset link to
          </label>
          <Input
            type="email"
            value={deliverTo}
            onChange={(e) => setDeliverTo(e.target.value)}
            data-testid="admin-bs-admin-deliver-to"
            placeholder="admin@yourcompany.com"
            className="h-8 text-[12px]"
          />
        </div>
        <Button size="sm" onClick={rotate} disabled={busy || !deliverTo.includes('@')}
                 data-testid="admin-bs-admin-rotate"
                 className="gap-1.5">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
          Send admin reset link
        </Button>
      </div>
    </div>
  );
}

function CustomerRow({ u, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);

  const trigger = async () => {
    if (!(await openConfirm({
      title: `Send reset link to ${u.email}?`,
      description: 'A one-hour link will be emailed to the customer. No password or bearer token is shown in the admin browser.',
      confirmLabel: 'Send reset link',
    }))) return;
    setBusy(true);
    try {
      const r = await http.post(`/admin/bookstore/users/${u.id}/reset-password`, { mode: 'email' })
        .then((res) => res.data);
      toast.success(r.delivered ? `Reset email sent to ${u.email}.` : 'Reset link requested.');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed.');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await http.delete(`/admin/bookstore/users/${u.id}`);
      toast.success(`Removed ${u.email}.`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed.');
    } finally { setRemoving(false); }
  };

  return (
    <div className="border-b border-border last:border-0 px-3 py-2"
          data-testid={`admin-bs-customer-row-${u.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[200px] flex-1">
          <div className="text-[12.5px] font-medium">{u.name || u.email}</div>
          <div className="text-[10.5px] text-muted-foreground">{u.email} · <span className="font-mono-stat">{u.id}</span></div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Maven</span>
          <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${
            u.maven_status === 'active'      ? 'bg-emerald-500/20 text-emerald-600' :
            u.maven_status === 'past_due'    ? 'bg-amber-500/20 text-amber-600' :
            u.maven_status === 'cancelled'   ? 'bg-red-500/20 text-red-600' :
            'bg-secondary text-muted-foreground'
          }`}>
            {u.maven_status || 'none'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={trigger} disabled={busy}
                   data-testid={`admin-bs-customer-reset-${u.id}`}
                   className="h-7 gap-1.5 text-[11.5px]">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
            Send link
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" disabled={removing}
                       data-testid={`admin-bs-customer-remove-${u.id}`}
                       className="h-7 gap-1.5 text-[11.5px] text-destructive hover:text-destructive">
                {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Remove
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Remove {u.name || u.email}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes the reader account, their shelf, sessions, and orders.
                  {u.stripe_customer_id
                    ? ' This reader has a Stripe record — cancel/refund in Stripe first.'
                    : ' They have no Stripe account, so this is safe to do now.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep</AlertDialogCancel>
                <AlertDialogAction
                  onClick={remove}
                  disabled={!!u.stripe_customer_id}
                  data-testid={`admin-bs-customer-remove-confirm-${u.id}`}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Remove reader
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

export default function BookstoreAdminSection() {
  const [open, setOpen] = useState(true);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [purgePreview, setPurgePreview] = useState(null);
  const [purging, setPurging] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get('/admin/bookstore/users?limit=500').then((res) => res.data);
      setUsers(r.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load bookstore customers.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const previewPurge = async () => {
    setPurging(true);
    try {
      const r = await http.post('/admin/bookstore/users/purge-test-accounts?dry_run=true')
        .then((res) => res.data);
      setPurgePreview(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not preview purge.');
    } finally { setPurging(false); }
  };

  const executePurge = async () => {
    setPurging(true);
    try {
      const r = await http.post('/admin/bookstore/users/purge-test-accounts?dry_run=false')
        .then((res) => res.data);
      toast.success(`Removed ${r.deleted} test account${r.deleted === 1 ? '' : 's'}.`);
      setPurgePreview(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge failed.');
    } finally { setPurging(false); }
  };

  const filtered = users.filter((u) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (u.email || '').toLowerCase().includes(q)
        || (u.name  || '').toLowerCase().includes(q)
        || (u.id    || '').toLowerCase().includes(q);
  });

  return (
    <section className="mt-6 space-y-3" data-testid="admin-bookstore-section">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="admin-bookstore-section-toggle"
        className="flex w-full items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-left text-[13px] font-semibold transition hover:bg-secondary/40"
      >
        <ShoppingBag className="h-4 w-4 text-primary" />
        Bookstore Admin &amp; Customers
        <span className="ml-auto text-[10.5px] text-muted-foreground">
          {users.length} customer{users.length === 1 ? '' : 's'}
        </span>
        {open ? <ChevronUp className="h-4 w-4 opacity-60" /> : <ChevronDown className="h-4 w-4 opacity-60" />}
      </button>

      {open && (
        <>
          <AdminPasswordCard />

          {/* Purge test accounts */}
          <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20 p-3" data-testid="admin-bs-purge-card">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 flex-1">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                <div>
                  <div className="text-[12.5px] font-semibold text-amber-900 dark:text-amber-200">Purge test accounts</div>
                  <div className="text-[11px] text-amber-700 dark:text-amber-400">
                    Removes all accounts with @example.com emails or test-pattern names. Stripe-linked accounts are always skipped.
                  </div>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={previewPurge} disabled={purging}
                       data-testid="admin-bs-purge-preview"
                       className="h-7 gap-1.5 text-[11.5px] border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-300">
                {purging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                Preview
              </Button>
            </div>

            {purgePreview && (
              <div className="mt-3 rounded border border-amber-200 dark:border-amber-900/50 bg-white dark:bg-background p-3 space-y-2"
                   data-testid="admin-bs-purge-preview-result">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-medium">
                    Found <strong>{purgePreview.safe_to_delete}</strong> test account{purgePreview.safe_to_delete === 1 ? '' : 's'} safe to delete
                    {purgePreview.skipped_stripe > 0 && (
                      <span className="text-muted-foreground"> · {purgePreview.skipped_stripe} skipped (Stripe)</span>
                    )}
                  </span>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setPurgePreview(null)}
                             className="h-7 text-[11px]" data-testid="admin-bs-purge-cancel">
                      Cancel
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" disabled={purging || purgePreview.safe_to_delete === 0}
                                 data-testid="admin-bs-purge-execute"
                                 className="h-7 gap-1.5 text-[11.5px] bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete {purgePreview.safe_to_delete}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {purgePreview.safe_to_delete} test accounts?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently removes {purgePreview.safe_to_delete} test reader account{purgePreview.safe_to_delete === 1 ? '' : 's'} and all their data (shelf, sessions, orders).
                            Accounts with Stripe records ({purgePreview.skipped_stripe}) are excluded.
                            This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={executePurge}
                            data-testid="admin-bs-purge-confirm"
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete all {purgePreview.safe_to_delete}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-0.5">
                  {purgePreview.accounts.slice(0, 50).map((a) => (
                    <div key={a.id} className="text-[10.5px] text-muted-foreground font-mono-stat truncate">
                      {a.name || '—'} · {a.email}
                    </div>
                  ))}
                  {purgePreview.accounts.length > 50 && (
                    <div className="text-[10.5px] text-muted-foreground">… and {purgePreview.accounts.length - 50} more</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-md border border-border bg-card" data-testid="admin-bs-customers-card">
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Filter by email, name, or user id…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="admin-bs-customers-filter"
                className="h-7 text-[12px]"
              />
              <Button size="sm" variant="outline" onClick={reload} disabled={loading}
                       data-testid="admin-bs-customers-refresh"
                       className="h-7 gap-1.5 text-[11.5px]">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                Refresh
              </Button>
            </div>
            {loading ? (
              <div className="px-3 py-4 text-[11.5px] text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-[11.5px] text-muted-foreground">
                {users.length === 0
                  ? 'No bookstore customers yet. They appear once they register at /bookstore.'
                  : `No customers match "${filter}".`}
              </div>
            ) : (
              <div>
                {filtered.map((u) => (
                  <CustomerRow key={u.id} u={u} onChanged={reload} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
